import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Search, RotateCcw, Download, RefreshCw, Eye, Copy, ChevronDown , ChevronUp } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import Select from 'react-select';
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import addDays from "date-fns/addDays";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import startOfYear from "date-fns/startOfYear"; 
import endOfYear from "date-fns/endOfYear";
import * as XLSX from 'xlsx';
import apiClient from './api/client';
import { getAllUsers } from './services/masterDataService';
import { useCurrentDomain } from "./context/DomainContext";

const BRAND_GREEN = '#0aa375';

const customSelectStyles = {
    control: (base, state) => ({
        ...base, height: '32px', minHeight: '32px', fontSize: '13px',
        borderColor: state.isFocused ? BRAND_GREEN : '#dee2e6',
        boxShadow: 'none', borderRadius: '4px',
        '&:hover': { borderColor: BRAND_GREEN },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    indicatorsContainer: (base) => ({ ...base, height: '32px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 4px', color: '#6c757d' }),
    clearIndicator: (base) => ({ ...base, padding: '0 4px', color: '#6c757d' }),
    input: (base) => ({ ...base, margin: '0px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base, fontSize: '13px',
        backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
        color: state.isSelected ? 'white' : '#333', cursor: 'pointer',
    }),
};

const predefinedBottomRanges = [
  { label: "Today", value: [new Date(), new Date()] },
  { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
  { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
  { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
  { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
  { label: "Last month", value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))] },
  { label: "This year", value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)] },
  { label: "Last year", value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear() - 1, 11, 31)] },
];

// 12 columns — indices 0–11
const COLUMNS = [
    { label: 'Log Date',           key: 'logDate' },        // 0
    { label: 'Log Type',           key: 'level' },          // 1
    { label: 'User',               key: 'username' },       // 2
    { label: 'Doctor ID',          key: 'doctorID' },       // 3
    { label: 'Source Row',         key: 'sourcelocation' }, // 4
    { label: 'Application Name',   key: 'appname' },        // 5
    { label: 'Event ID',           key: 'eventID' },        // 6
    { label: 'Event/Logs',         key: 'eventName' },      // 7
    { label: 'Source API',         key: 'apIdomain' },      // 8
    { label: 'Function Name',      key: 'functionName' },   // 9
    { label: 'Module',             key: 'filename' },       // 10
    { label: 'Trace Context Meta', key: 'extraInfo' },      // 11
];

// Backend already sorts DESC — this just ensures merge order on load-more is preserved
const sortByLogDateDesc = (logs) =>
    [...logs].sort((a, b) => {
        const da = a.logDate ? new Date(a.logDate).getTime() : (a.timestamp ? new Date(a.timestamp).getTime() : 0);
        const db = b.logDate ? new Date(b.logDate).getTime() : (b.timestamp ? new Date(b.timestamp).getTime() : 0);
        return db - da;
    });

const MixPanelLogs = () => {
    const { domain } = useCurrentDomain();

    const [logs, setLogs]                           = useState([]);
    const [loading, setLoading]                     = useState(false);
    const [searchLoading, setSearchLoading]         = useState(false);
    const [continuationToken, setContinuationToken] = useState(null);
    const [hasMore, setHasMore]                     = useState(false);

    // 12 widths matching 12 COLUMNS exactly
    const [colWidths, setColWidths] = useState([155, 85, 140, 130, 160, 160, 100, 200, 180, 160, 120, 190]);

    // Filters
    const [globalSearch, setGlobalSearch]     = useState('');
    const [users, setUsers]                   = useState([]);
    const [usersLoading, setUsersLoading]     = useState(false);
    const [selectedUser, setSelectedUser]     = useState(null);
    const [appName, setAppName]               = useState('');
    const [eventId, setEventId]               = useState('');
    const [doctorId, setDoctorId]             = useState('');
    const [fromDate, fromDateSet]             = useState(null);
    const [toDate, toDateSet]                 = useState(null);
    const [isResetHovered, setIsResetHovered] = useState(false);
    const [isExcelHovered, setIsExcelHovered] = useState(false);

    const [modalData, setModalData] = useState({ isOpen: false, title: '', content: '' });
    const [isCopied, setIsCopied]   = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Column resize refs
    const activeIndex = useRef(null);
    const startX      = useRef(0);
    const startWidth  = useRef(0);

    const handleMouseDown = (index, e) => {
        activeIndex.current = index;
        startX.current      = e.clientX;
        startWidth.current  = colWidths[index];
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = useCallback((e) => {
        if (activeIndex.current === null) return;
        const computedWidth = Math.max(60, startWidth.current + (e.clientX - startX.current));
        setColWidths((prev) => {
            const updated = [...prev];
            updated[activeIndex.current] = computedWidth;
            return updated;
        });
    }, []);

    const handleMouseUp = useCallback(() => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        activeIndex.current = null;
        document.body.style.userSelect = '';
    }, [handleMouseMove]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    const buildParams = useCallback((token) => {
        // Fix: set ToDate to end of selected day so same-day range works
        let adjustedToDate = toDate;
        if (toDate) {
            adjustedToDate = new Date(toDate);
            adjustedToDate.setHours(23, 59, 59, 999);
        }

        return {
            pageSize:          50,
            SearchText:        globalSearch.trim() || undefined,
            UserID:            selectedUser?.value || undefined,
            AppName:           appName.trim() || undefined,
            EventID:           eventId.trim() || undefined,
            DoctorID:          doctorId.trim() || undefined,
            FromDate:          fromDate         ? fromDate.toISOString()         : undefined,
            ToDate:            adjustedToDate   ? adjustedToDate.toISOString()   : undefined,
            ContinuationToken: token || undefined,
        };
    }, [globalSearch, selectedUser, appName, eventId, doctorId, fromDate, toDate]);

    const fetchLogs = useCallback(async (token, isNewSearch) => {
        const setLoader = isNewSearch ? setSearchLoading : setLoading;
        setLoader(true);
        try {
            const res     = await apiClient.get('/api/MixPanelLog', { params: buildParams(token) });
            const newLogs = res.data.logs || [];

            setLogs(prev => {
                // Backend already returns sorted data — merge and re-sort to handle load-more appends
                const merged = isNewSearch ? newLogs : [...prev, ...newLogs];
                return sortByLogDateDesc(merged);
            });

            setContinuationToken(res.data.continuationToken || null);
            setHasMore(!!res.data.hasMore);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load logs');
            console.error(err);
        } finally {
            setLoader(false);
        }
    }, [buildParams]);

    const fetchUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const data = await getAllUsers(domain);
            setUsers(data.map(u => ({ value: u.code, label: u.description })));
        } catch {
            toast.error('Failed to load users.');
        } finally {
            setUsersLoading(false);
        }
    }, [domain]);

    useEffect(() => { if (domain) fetchUsers(); }, [domain, fetchUsers]);

    useEffect(() => {
        setLogs([]);
        setContinuationToken(null);
        setHasMore(false);
        fetchLogs(null, true);
    }, [domain]);

    const handleSearch = () => {
        setLogs([]);
        setContinuationToken(null);
        setHasMore(false);
        fetchLogs(null, true);
    };

    const handleReset = () => {
        setGlobalSearch('');
        setSelectedUser(null);
        setAppName('');
        setEventId('');
        setDoctorId('');
        fromDateSet(null);
        toDateSet(null);
        setLogs([]);
        setContinuationToken(null);
        setHasMore(false);
        setTimeout(() => fetchLogs(null, true), 0);
    };

    const handleLoadMore = () => {
        if (continuationToken && hasMore && !loading)
            fetchLogs(continuationToken, false);
    };

    const exportAllToExcel = () => {
        if (!logs.length) { toast.error('No data to export'); return; }
        const tid = toast.loading('Preparing export...');
        try {
            const exportData = logs.map(log => ({
                TIMESTAMP:   log.timestamp      ? new Date(log.timestamp).toLocaleString()  : '',
                LOG_DATE:    log.logDate         ? new Date(log.logDate).toLocaleString()    : '',
                LOG_TYPE:    log.level           || '',
                APPLICATION: log.appname         || '',
                EVENT:       log.eventName       || '',
                EVENT_ID:    log.eventID         || '',
                DOCTOR_ID:   log.doctorID        || '',
                FUNCTION:    log.functionName    || '',
                MODULE:      log.filename        || '',
                SOURCE_ROW:  log.sourcelocation  || '',
                USER_ID:     log.userID          || '',
                USERNAME:    log.username        || '',
                SOURCE_API:  log.apIdomain       || '',
                EXTRA_INFO:  log.extraInfo       || '',
            }));
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'MixPanelLogs');
            XLSX.writeFile(wb, `MixPanelLogs_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${exportData.length} record${exportData.length !== 1 ? 's' : ''}`);
        } catch {
            toast.error('Failed to export');
        } finally {
            toast.dismiss(tid);
        }
    };

    const copyToClipboard = async () => {
        if (!modalData.content) return;
        try {
            await navigator.clipboard.writeText(modalData.content);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 5000);
        } catch { toast.error('Copy failed'); }
    };

    const getLevelBadgeStyles = (lvl) => {
        switch ((lvl || '').toUpperCase()) {
            case 'ERROR': return { backgroundColor: '#fde8e8', color: '#e02424' };
            case 'WARN':  return { backgroundColor: '#fef3c7', color: '#d97706' };
            case 'INFO':  return { backgroundColor: '#e6f7f2', color: BRAND_GREEN };
            default:      return { backgroundColor: '#f1f5f9', color: '#475569' };
        }
    };

    const filterInputStyle = { height: '32px', fontSize: '12px', padding: '4px 8px', boxShadow: 'none' };
    const filterLabelStyle = { fontSize: '10px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px', display: 'block' };

    // Helper: td style using correct column index
    const tdStyle = (i) => ({ width: `${colWidths[i]}px`, maxWidth: `${colWidths[i]}px` });

    return (
        <div className="bg-light min-vh-100">
            <Toaster position="top-right" />

            <div className="card shadow-sm border-0 rounded-3 bg-white">

                {/* Filter bar */}
                <div className="border-bottom px-2 py-2" style={{ backgroundColor: '#fcfcfc' }}>
                    {showFilters && (
                    <>
                    <div className="d-flex gap-2 align-items-end flex-wrap mb-2">

                        {/* Search */}
                        <div style={{ flex: '1 1 190px', minWidth: '180px' }}>
                            <label style={filterLabelStyle}>Search Event Text</label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0 text-muted py-0" style={{ height: '32px' }}>
                                    <Search size={14} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0 ps-1"
                                    style={{ height: '32px', fontSize: '12px', boxShadow: 'none' }}
                                    placeholder="Search Event Text..."
                                    value={globalSearch}
                                    onChange={(e) => setGlobalSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                        </div>

                        {/* User */}
                        <div style={{ flex: '1 1 150px', minWidth: '140px' }}>
                            <label style={filterLabelStyle}>User</label>
                            <Select
                                options={users}
                                value={selectedUser}
                                onChange={setSelectedUser}
                                isLoading={usersLoading}
                                placeholder="Select user..."
                                isSearchable isClearable
                                menuPortalTarget={document.body}
                                styles={customSelectStyles}
                            />
                        </div>

                        {/* App Name */}
                        <div style={{ flex: '1 1 140px', minWidth: '130px' }}>
                            <label style={filterLabelStyle}>App Name</label>
                            <input type="text" className="form-control form-control-sm" style={filterInputStyle}
                                placeholder="e.g. doctor.gyb" value={appName}
                                onChange={(e) => setAppName(e.target.value)} />
                        </div>

                        {/* Event ID */}
                        <div style={{ flex: '1 1 110px', minWidth: '100px' }}>
                            <label style={filterLabelStyle}>Event ID</label>
                            <input type="text" className="form-control form-control-sm" style={filterInputStyle}
                                placeholder="e.g. 1392947" value={eventId}
                                onChange={(e) => setEventId(e.target.value)} />
                        </div>

                        {/* Doctor ID */}
                        <div style={{ flex: '1 1 140px', minWidth: '130px' }}>
                            <label style={filterLabelStyle}>Doctor ID</label>
                            <input type="text" className="form-control form-control-sm" style={filterInputStyle}
                                placeholder="e.g. DT0000000001" value={doctorId}
                                onChange={(e) => setDoctorId(e.target.value)} />
                        </div>

                        {/* Date Range */}
                        <div style={{ flex: '1 1 200px', minWidth: '190px' }}>
                        <label style={filterLabelStyle}>Date Range</label>
                        <DateRangePicker
                            value={[fromDate, toDate]}
                            onChange={(value) => {
                            if (!value || value.length !== 2) {
                                fromDateSet(null);
                                toDateSet(null);
                                return;
                            }
                            const [start, end] = value;
                            fromDateSet(start);
                            toDateSet(end);
                            }}
                            ranges={predefinedBottomRanges}
                            placeholder="Select Date Range"
                            style={{ width: '100%' }}
                            format="MM/dd/yyyy"
                            size="sm"
                            cleanable
                            placement="bottomStart"
                            preventOverflow
                            showOneCalendar={isMobile}
                        />
                        </div>

                    </div>

                    {/* Action buttons */}
                    <div className="d-flex justify-content-end gap-2 pt-2 border-top flex-wrap">
                        <button
                        className="btn text-white fw-medium d-flex align-items-center gap-1 px-3"
                        style={{ 
                            backgroundColor: BRAND_GREEN, height: '32px', 
                            width: window.innerWidth < 768 ? '48px' : 'auto',
                            justifyContent: 'center', fontSize: '12px'
                        }}
                        onClick={handleSearch} 
                        disabled={loading || searchLoading}
                        title="Search"
                        >
                        {searchLoading ? (
                            <RefreshCw size={22} className="animate-spin" />
                        ) : window.innerWidth < 768 ? (
                            <Search size={22} />
                        ) : (
                            <>
                            <Search size={16} /> Search
                            </>
                        )}
                        </button>
                        <button
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 px-3"
                        style={{ 
                            height: '32px', justifyContent: 'center',
                            width: window.innerWidth < 768 ? '48px' : 'auto',                            
                        }}
                        onClick={handleReset}
                        title="Reset"
                        >
                        {window.innerWidth < 768 ? <RotateCcw size={22} /> : <RotateCcw size={16} />}
                        </button>
                        <button
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 px-3"
                        style={{ 
                            height: '32px', justifyContent: 'center',
                            width: window.innerWidth < 768 ? '48px' : 'auto'                            
                        }}
                        onClick={exportAllToExcel}
                        title="Export To Excel"
                        >
                        {window.innerWidth < 768 ? <Download size={22} /> :<Download size={16} /> }
                        </button>

                    <button
                        className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1 ms-auto"
                        style={{ height: '32px', fontSize: '12px' }}
                        onClick={() => setShowFilters(false)}
                    >
                        Hide Filters <ChevronDown size={14} />
                    </button>
                    </div>
                    </>
                )}

                    <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
                        {!showFilters && (
                        <button
                            className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1"
                            style={{ fontSize: '12px', height: '28px' }}
                            onClick={() => setShowFilters(true)}
                        >
                            Show Filters <ChevronUp size={14} />
                        </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="table-responsive scroll-hidden" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <table
                        className="table table-hover align-middle mb-0 d-none d-lg-table"
                        style={{ tableLayout: 'fixed', fontSize: '12.5px', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
                    >
                        <thead className="table-light sticky-top border-bottom" style={{ zIndex: 10 }}>
                            <tr>
                                {COLUMNS.map((col, index) => (
                                    <th
                                        key={index}
                                        className="text-secondary fw-bold text-uppercase py-1 px-2 position-relative"
                                        style={{
                                            width: `${colWidths[index]}px`,
                                            minWidth: `${colWidths[index]}px`,
                                            maxWidth: `${colWidths[index]}px`,
                                            fontSize: '10.5px',
                                            letterSpacing: '0.4px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        <span className="d-flex align-items-center gap-1">
                                            {col.label}
                                            {index === 0 && (
                                                <span style={{ fontSize: '9px', color: BRAND_GREEN, fontWeight: 700 }}>↓</span>
                                            )}
                                        </span>
                                        <div
                                            onMouseDown={(e) => handleMouseDown(index, e)}
                                            style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 11 }}
                                            className="column-resizer-handle"
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 && !loading && !searchLoading ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="text-center py-4 text-muted border-0">
                                        No matching logs found.
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log, idx) => {
                                    const badgeStyles = getLevelBadgeStyles(log.level);
                                    return (
                                        <tr key={`${log.partitionKey || idx}-${log.rowKey || idx}`} style={{ height: '30px' }}>

                                            {/* 0 — Log Date */}
                                            <td className="text-secondary py-0 px-2 text-truncate" style={tdStyle(0)}>
                                                {log.logDate ? new Date(log.logDate).toLocaleString() : '—'}
                                            </td>

                                            {/* 1 — Log Type */}
                                            <td className="py-0 px-2" style={tdStyle(1)}>
                                                {log.level ? (
                                                    <span className="badge fw-bold px-15 py-05" style={{ ...badgeStyles, borderRadius: '3px', fontSize: '10px' }}>
                                                        {log.level.toUpperCase()}
                                                    </span>
                                                ) : '—'}
                                            </td>

                                            {/* 2 — User */}
                                            <td className="py-0 px-2 text-truncate fw-medium text-dark" title={log.userID} style={tdStyle(2)}>
                                                {log.username || '—'}
                                            </td>

                                            {/* 3 — Doctor ID */}
                                            <td className="py-0 px-2 text-truncate text-secondary" title={log.doctorID} style={tdStyle(3)}>
                                                {log.doctorID || '—'}
                                            </td>

                                            {/* 4 — Source Row */}
                                            <td className="py-0 px-2 text-truncate text-secondary" title={log.sourcelocation} style={tdStyle(4)}>
                                                {log.sourcelocation || '—'}
                                            </td>

                                            {/* 5 — Application Name */}
                                            <td className="py-0 px-2 text-truncate text-dark fw-medium" title={log.appname} style={tdStyle(5)}>
                                                {log.appname || '—'}
                                            </td>

                                            {/* 6 — Event ID */}
                                            <td className="py-0 px-2 text-truncate text-secondary" title={String(log.eventID || '')} style={tdStyle(6)}>
                                                {log.eventID || '—'}
                                            </td>

                                            {/* 7 — Event */}
                                            <td className="py-0 px-2" style={tdStyle(7)}>
                                                <div className="d-flex align-items-center justify-content-between gap-1">
                                                    <span className="text-truncate d-block w-100" style={{ color: BRAND_GREEN }} title={log.eventName}>
                                                        {log.eventName || '—'}
                                                    </span>
                                                    {log.eventName && log.eventName.length > 20 && (
                                                        <button className="btn btn-sm btn-link p-0 flex-shrink-0" style={{ color: BRAND_GREEN }}
                                                            onClick={() => setModalData({ isOpen: true, title: 'Event Detail', content: log.eventName })}>
                                                            <Eye size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                            {/* 8 — Source API */}
                                            <td className="py-0 px-2 text-truncate text-secondary" title={log.apIdomain} style={tdStyle(8)}>
                                                {log.apIdomain || '—'}
                                            </td>

                                            {/* 9 — Function Name */}
                                            <td className="py-0 px-2 text-truncate font-monospace" title={log.functionName} style={{ ...tdStyle(9), fontSize: '11.5px' }}>
                                                {log.functionName || '—'}
                                            </td>

                                            {/* 10 — Module */}
                                            <td className="py-0 px-2 text-truncate text-secondary" title={log.filename} style={tdStyle(10)}>
                                                {log.filename || '—'}
                                            </td>

                                            {/* 11 — Trace Context Meta */}
                                            <td className="py-0 px-2" style={tdStyle(11)}>
                                                <div className="d-flex align-items-center justify-content-between gap-1">
                                                    <span className="text-muted text-truncate d-block w-100 font-monospace" style={{ fontSize: '11px' }} title={log.extraInfo}>
                                                        {log.extraInfo || '—'}
                                                    </span>
                                                    {log.extraInfo && log.extraInfo.length > 20 && (
                                                        <button className="btn btn-sm btn-link p-0 flex-shrink-0" style={{ color: BRAND_GREEN }}
                                                            onClick={() => setModalData({ isOpen: true, title: 'Trace Context Meta', content: log.extraInfo })}>
                                                            <Eye size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    {/* CARD VIEW  */}
                    <div className="d-lg-none" style={{ padding: '0 8px' }}>
                    {loading || searchLoading ? (
                        <div className="text-center py-5">
                        <span className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} />
                        <div className="mt-2 text-muted">Loading logs...</div>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-5 text-muted">No matching logs found.</div>
                    ) : (
                        logs.map((log, idx) => {
                        const badgeStyles = getLevelBadgeStyles(log.level);
                        return (
                            <div 
                            key={idx} 
                            style={{
                                background: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '10px',
                                padding: '14px 16px',
                                marginBottom: '12px',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                            }}
                            >
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>LOG DATE</div>
                                <div style={{ fontSize: '14px', fontWeight: 500 }}>
                                    {log.logDate ? new Date(log.logDate).toLocaleString() : '—'}
                                </div>
                                </div>
                                {log.level && (
                                <span 
                                    className="badge fw-bold px-15 py-05" 
                                    style={{ 
                                    ...badgeStyles, 
                                    borderRadius: '4px', 
                                    fontSize: '11px',
                                    alignSelf: 'flex-start'
                                    }}
                                >
                                    {log.level.toUpperCase()}
                                </span>
                                )}
                            </div>

                            <div className="row g-2">
                                <div className="col-12">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>USER</div>
                                <div style={{ fontSize: '14px', fontWeight: 500 }}>{log.username || '—'}</div>
                                </div>

                                <div className="col-6">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>DOCTOR ID</div>
                                <div style={{ fontSize: '13.5px' }}>{log.doctorID || '—'}</div>
                                </div>

                                <div className="col-6">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>EVENT ID</div>
                                <div style={{ fontSize: '13.5px' }}>{log.eventID || '—'}</div>
                                </div>

                                <div className="col-12">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>APPLICATION NAME</div>
                                <div style={{ fontSize: '13.5px' }}>{log.appname || '—'}</div>
                                </div>

                                <div className="col-12">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>SOURCE ROW</div>
                                <div style={{ fontSize: '13.5px' }}>{log.sourcelocation || '—'}</div>
                                </div>

                                <div className="col-12">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>SOURCE API</div>
                                <div style={{ fontSize: '13.5px' }}>{log.apIdomain || '—'}</div>
                                </div>

                                <div className="col-12">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>FUNCTION NAME</div>
                                <div style={{ fontSize: '13.5px', fontFamily: 'monospace' }}>{log.functionName || '—'}</div>
                                </div>

                                <div className="col-12">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>MODULE</div>
                                <div style={{ fontSize: '13.5px' }}>{log.filename || '—'}</div>
                                </div>

                                <div className="col-12">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>EVENT / LOGS</div>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div style={{ fontSize: '13.5px', flex: 1, wordBreak: 'break-word' }}>
                                    {log.eventName || '—'}
                                    </div>
                                    {log.eventName && log.eventName.length > 20 && (
                                    <button 
                                        className="btn btn-sm p-1 ms-2 flex-shrink-0" 
                                        style={{ color: BRAND_GREEN }}
                                        onClick={() => setModalData({ isOpen: true, title: 'Event Detail', content: log.eventName })}
                                    >
                                        <Eye size={16} />
                                    </button>
                                    )}
                                </div>
                                </div>

                                <div className="col-12">
                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>TRACE CONTEXT META</div>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div style={{ fontSize: '13px', flex: 1, wordBreak: 'break-word', fontFamily: 'monospace' }}>
                                    {log.extraInfo || '—'}
                                    </div>
                                    {log.extraInfo && log.extraInfo.length > 20 && (
                                    <button 
                                        className="btn btn-sm p-1 ms-2 flex-shrink-0" 
                                        style={{ color: BRAND_GREEN }}
                                        onClick={() => setModalData({ isOpen: true, title: 'Trace Context Meta', content: log.extraInfo })}
                                    >
                                        <Eye size={16} />
                                    </button>
                                    )}
                                </div>
                                </div>
                            </div>
                            </div>
                        );
                        })
                    )}
                    </div>

                    {hasMore && (
                        <div className="text-center py-3 border-top bg-white">
                            <button
                                className="btn btn-sm d-flex align-items-center gap-1 px-3 mx-auto"
                                style={{ height: '30px', fontSize: '12px', fontWeight: 500, backgroundColor: '#fff', border: `1px solid ${BRAND_GREEN}`, color: BRAND_GREEN, borderRadius: '4px' }}
                                onClick={handleLoadMore} disabled={loading}
                            >
                                {loading ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <ChevronDown size={12} />}
                                {loading ? 'Loading...' : 'Load more'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-3 py-2 border-top d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ backgroundColor: '#fafafa' }}>
                    <span className="text-muted" style={{ fontSize: '12.5px' }}>
                        Showing <strong>{logs.length}</strong> record{logs.length !== 1 ? 's' : ''}
                        {hasMore && <span className="ms-1 text-muted">(more available)</span>}
                    </span>
                    {hasMore && (
                        <button
                            className="btn btn-sm d-flex align-items-center gap-1 px-3"
                            style={{ height: '30px', fontSize: '12.5px', fontWeight: 500, backgroundColor: '#fff', border: `1px solid ${BRAND_GREEN}`, color: BRAND_GREEN, borderRadius: '5px' }}
                            onClick={handleLoadMore} disabled={loading}
                        >
                            {loading ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <ChevronDown size={13} />}
                            {loading ? 'Loading...' : 'Load more'}
                        </button>
                    )}
                </div>
            </div>

            {/* Modal */}
            {modalData.isOpen && (
                <div
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1060,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px'
                    }}
                    onClick={(e) => e.target === e.currentTarget && setModalData({ isOpen: false, title: '', content: '' })}
                >
                    <div style={{
                        width: '100%', maxWidth: '720px', background: 'white', borderRadius: '10px',
                        overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                    }} onClick={e => e.stopPropagation()}>

                        {/* Green Header */}
                        <div style={{ 
                            background: BRAND_GREEN, color: 'white', padding: '12px 18px',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <div style={{ fontWeight: 700, fontSize: '15px' }}>{modalData.title}</div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button 
                                    onClick={copyToClipboard} 
                                    style={{ background: 'transparent', border: 'none', color: 'white', padding: '4px' }}
                                    title="Copy"
                                >
                                    {isCopied ? <span style={{ color: '#4ade80', fontSize: '18px', fontWeight: 'bold' }}>✓✓</span> : <Copy size={18} />}
                                </button>
                                <button 
                                    onClick={() => setModalData({ isOpen: false, title: '', content: '' })} 
                                    style={{ fontSize: '26px', background: 'none', border: 'none', color: 'white' }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div style={{ 
                            padding: '18px', 
                            fontSize: '13.5px', 
                            lineHeight: '1.55', 
                            background: '#f8f9fa', 
                            maxHeight: '68vh', 
                            overflowY: 'auto', 
                            whiteSpace: 'pre-wrap', 
                            fontFamily: 'Consolas, Monaco, monospace'
                        }}>
                            {modalData.content}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .column-resizer-handle { 
                    transition: background-color 0.15s ease; 
                }
                .column-resizer-handle:hover, 
                .column-resizer-handle:active { 
                    background-color: ${BRAND_GREEN} !important; 
                    width: 5px !important; 
                }
                .py-05 { padding-top: 0.125rem; padding-bottom: 0.125rem; }
                .px-15 { padding-left: 0.375rem; padding-right: 0.375rem; }
                .react-datepicker-popper { z-index: 1055 !important; }
                .scroll-hidden { scrollbar-width: none; -ms-overflow-style: none; }
                .scroll-hidden::-webkit-scrollbar { display: none; width: 0; height: 0; }
                .react-datepicker__input-container input {
                    height: 32px !important;
                    font-size: 12.5px !important;
                    padding: 6px 10px !important;
                    border: 1px solid #dee2e6;
                    border-radius: 4px;
                }

                .react-datepicker__input-container input:focus { 
                    border-color: ${BRAND_GREEN}; 
                    box-shadow: 0 0 0 2px ${BRAND_GREEN}22; 
                }
                @media (max-width: 768px) {
                    .rsuite .rs-picker-date-range { width: 100% !important; }      
                    .rs-picker-date-range .rs-picker-toggle { width: 100% !important; padding: 8px 12px !important; font-size: 13px !important; }      
                    .rs-picker-date-range .rs-picker-menu { width: 100% !important; max-width: 100vw !important; left: 0 !important; right: 0 !important; margin: 4px 0 !important; }      
                    .rs-picker-date-range .rs-picker-daterange-calendar-group > div:last-child { display: none !important; }
                }
            `}</style>
        </div>
    );
};

export default MixPanelLogs;